import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { RequestAccessModal } from "@/components/RequestAccessModal";
import { 
  ArrowRight, 
  Target, 
  Building2, 
  Calendar,
  CheckCircle,
  TrendingUp,
  Users,
  FileText,
  Mic,
  Coins,
  Settings,
  Lock,
  Rocket,
  Gauge,
  Layers
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function FoundersLanding() {
  const navigate = useNavigate();
  const [showAdminDialog, setShowAdminDialog] = useState(false);
  const [pinCode, setPinCode] = useState("");
  const [pinError, setPinError] = useState(false);
  const [accessFeature, setAccessFeature] = useState(null); // 'office_hours' | 'event_intel' | 'aspiring' | null
  const [content, setContent] = useState({
    hero_title_line1: "The Operating System",
    hero_title_line2: "for Therapeutics Founders",
    hero_subtitle: "Built exclusively for UK therapeutic startups. Operon helps you refine your strategy across 12 modules, matches you with the right investors and pharma partners, and points you to the funding and events that actually fit — end-to-end, in one place.",
    ai_tools_title: "AI-Powered Preparation Tools",
    ai_tools_subtitle: "Practice and refine before your most important meetings",
    aspiring_title: "Are you an aspiring founder?",
    aspiring_subtitle: "Access Operon Start to understand how to make the jump into UK therapeutics. Find venture builders, connect with co-founders, and assess your readiness."
  });

  useEffect(() => {
    // Load homepage content from database
    fetch(`${BACKEND_URL}/api/homepage-content`)
      .then(res => res.json())
      .then(data => setContent(prev => ({ ...prev, ...data })))
      .catch(err => console.error("Failed to load homepage content:", err));
  }, []);

  const CORRECT_PIN = "310721";

  const handleAdminClick = () => {
    setShowAdminDialog(true);
    setPinCode("");
    setPinError(false);
  };

  const handlePinSubmit = (e) => {
    e.preventDefault();
    if (pinCode === CORRECT_PIN) {
      setShowAdminDialog(false);
      navigate('/admin-area');
    } else {
      setPinError(true);
      setPinCode("");
    }
  };

  const handlePinChange = (e) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
    setPinCode(value);
    setPinError(false);
  };

  const handleAspiringClick = () => {
    setAccessFeature('aspiring');
  };

  const features = [
    {
      icon: Layers,
      title: "Strategy Refinement — 12 Modules",
      description: "Structure and stress-test your thinking across Market, Clinical, Regulatory, Access, Technology, Business Model, Team, Milestones and Fundraising — each with AI-guided prompts and evidence look-ups."
    },
    {
      icon: Gauge,
      title: "AI Readiness Diagnostic",
      description: "A 12-axis Readiness Radar and 3-axis Investors' Risk View score your company from your captured strategy — with a prioritised to-do list showing exactly which modules to strengthen next."
    },
    {
      icon: Target,
      title: "AI-Powered Investor Matching",
      description: "Find investors whose thesis, stage focus, and portfolio align with your therapeutic area. Transparent scoring — no more spray-and-pray outreach."
    },
    {
      icon: Building2,
      title: "Pharma Partnership Intelligence",
      description: "Identify pharma companies actively seeking external innovation in your indication. Understand their strategic priorities before you reach out."
    },
    {
      icon: Coins,
      title: "Grant Recommendations",
      description: "Discover non-dilutive funding opportunities matched to your development stage and therapeutic focus."
    },
    {
      icon: Calendar,
      title: "Strategic Events",
      description: "Know which conferences and events to attend based on investor and pharma presence relevant to your company."
    }
  ];

  const benefits = [
    "12 structured strategy modules with AI-guided prompts and evidence look-ups",
    "Readiness Radar + Investors' Risk View — see your weakest axes at a glance",
    "Prioritised, module-scoped to-do lists you can act on this week",
    "Transparent investor matching with thesis, stage and portfolio conflict checks",
    "Pharma partnership signals and warm introduction pathways",
    "Non-dilutive grants and strategic events matched to your stage"
  ];

  return (
    <div className="min-h-screen bg-[#121318]">
      {/* Navigation */}
      <nav className="max-w-7xl mx-auto w-full px-6 py-6 flex items-center justify-between">
        <img 
          src="/logo-complete.png" 
          alt="Operon" 
          className="h-[60px] object-contain"
        />
        
        <div className="w-10" /> {/* Spacer for centering */}
        
        <Button 
          onClick={handleAdminClick}
          variant="ghost"
          size="icon"
          className="text-slate-500 hover:text-slate-300 hover:bg-slate-800"
          data-testid="btn-admin"
        >
          <Settings className="w-5 h-5" />
        </Button>
      </nav>

      {/* Hero Section */}
      <section className="max-w-7xl mx-auto px-6 py-12 md:py-16">
        <div className="text-center max-w-4xl mx-auto">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6">
            {content.hero_title_line1}<br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-teal-400 to-emerald-400">
              {content.hero_title_line2}
            </span>
          </h1>
          
          <p className="text-lg sm:text-xl text-slate-400 mb-10 max-w-2xl mx-auto">
            {content.hero_subtitle}
          </p>

          <Button 
            onClick={() => navigate('/auth')}
            className="bg-teal-500 hover:bg-teal-600 text-white px-8 py-6 h-auto text-lg rounded-xl"
            data-testid="btn-sign-in"
          >
            Sign In
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </section>

      {/* AI Tools Section */}
      <section className="border-y border-slate-700 bg-gradient-to-r from-teal-900/20 to-emerald-900/20">
        <div className="max-w-7xl mx-auto px-6 py-12">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-white mb-2">{content.ai_tools_title}</h2>
            <p className="text-slate-400">{content.ai_tools_subtitle}</p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {/* AI Investor Office Hours */}
            <div 
              onClick={() => setAccessFeature('office_hours')}
              className="group cursor-pointer p-6 rounded-2xl bg-slate-800/60 border border-emerald-500/30 hover:border-emerald-500/60 transition-all hover:shadow-lg hover:shadow-emerald-500/10"
              data-testid="card-office-hours"
            >
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 rounded-xl bg-emerald-500/20 flex items-center justify-center group-hover:bg-emerald-500/30 transition-colors">
                  <FileText className="w-6 h-6 text-emerald-400" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white">AI-Investor Office Hours</h3>
                  <p className="text-emerald-400 text-sm">Live deck review</p>
                </div>
              </div>
              <p className="text-slate-400 text-sm mb-4">
                Upload your pitch deck and get real-time voice feedback from an AI investor. 
                Slide-by-slide review with honest first impressions and actionable improvements.
              </p>
              <div className="flex items-center text-emerald-400 text-sm font-medium group-hover:gap-2 transition-all">
                <span>Start Office Hours</span>
                <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
              </div>
            </div>

            {/* Event Investor Intelligence */}
            <div 
              onClick={() => setAccessFeature('event_intel')}
              className="group cursor-pointer p-6 rounded-2xl bg-slate-800/60 border border-cyan-500/30 hover:border-cyan-500/60 transition-all hover:shadow-lg hover:shadow-cyan-500/10"
              data-testid="card-event-intel"
            >
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 rounded-xl bg-cyan-500/20 flex items-center justify-center group-hover:bg-cyan-500/30 transition-colors">
                  <Mic className="w-6 h-6 text-cyan-400" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white">Event Investor Intelligence</h3>
                  <p className="text-cyan-400 text-sm">Conference prep</p>
                </div>
              </div>
              <p className="text-slate-400 text-sm mb-4">
                Upload any investor list from a conference. Get instant intelligence on each investor: 
                thesis fit, portfolio conflicts, and personalized talking points.
              </p>
              <div className="flex items-center text-cyan-400 text-sm font-medium group-hover:gap-2 transition-all">
                <span>Analyze Investor List</span>
                <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Problem Statement */}
      <section className="bg-slate-800/30">
        <div className="max-w-7xl mx-auto px-6 py-16">
          <div className="grid md:grid-cols-3 gap-8 text-center">
            <div>
              <div className="text-4xl font-bold text-teal-400 mb-2">12</div>
              <p className="text-slate-400">strategy modules to structure and stress-test your thinking end-to-end</p>
            </div>
            <div>
              <div className="text-4xl font-bold text-teal-400 mb-2">80%</div>
              <p className="text-slate-400">of founder time wasted on misaligned investor outreach — Operon fixes that</p>
            </div>
            <div>
              <div className="text-4xl font-bold text-teal-400 mb-2">6+ months</div>
              <p className="text-slate-400">typical fundraising cycle — start earlier with a stronger strategic foundation</p>
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="max-w-7xl mx-auto px-6 py-20">
        <div className="text-center mb-12">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            Everything you need — from strategy to signed term sheet
          </h2>
          <p className="text-slate-400 max-w-2xl mx-auto">
            Operon brings together AI strategy refinement, readiness diagnostics, investor and pharma intelligence, grants and events in one platform built specifically for UK therapeutic startups.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map((feature, i) => {
            const Icon = feature.icon;
            return (
              <div 
                key={i}
                className="p-6 rounded-2xl bg-slate-800/50 border border-slate-700 hover:border-teal-500/50 transition-colors"
              >
                <div className="w-12 h-12 rounded-xl bg-teal-500/10 flex items-center justify-center mb-4">
                  <Icon className="w-6 h-6 text-teal-400" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-2">{feature.title}</h3>
                <p className="text-slate-400 text-sm">{feature.description}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* Benefits Section */}
      <section className="max-w-7xl mx-auto px-6 py-16">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <div>
            <h2 className="text-3xl font-bold text-white mb-6">
              A stronger strategy makes every conversation count
            </h2>
            <p className="text-slate-400 mb-8">
              Operon doesn&apos;t just find matches — it first helps you build the strategy those matches will actually invest in. Refine your thinking across 12 modules, see your weakest axes on the Readiness Radar, then walk into investor and pharma meetings with a clear, defensible narrative.
            </p>
            <ul className="space-y-4">
              {benefits.map((benefit, i) => (
                <li key={i} className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-teal-400 flex-shrink-0 mt-0.5" />
                  <span className="text-slate-300">{benefit}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="bg-gradient-to-br from-slate-800 to-slate-800/50 rounded-2xl p-8 border border-slate-700">
            <div className="flex items-center gap-3 mb-6">
              <TrendingUp className="w-6 h-6 text-teal-400" />
              <span className="text-lg font-semibold text-white">Match Score Breakdown</span>
            </div>
            <div className="space-y-4">
              <div className="p-3 bg-green-500/10 border border-green-500/20 rounded-lg">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm text-green-400">Thesis Gate</span>
                  <CheckCircle className="w-4 h-4 text-green-400" />
                </div>
                <p className="text-xs text-slate-400">Thesis aligned (oncology, small molecule)</p>
              </div>
              <div className="p-3 bg-green-500/10 border border-green-500/20 rounded-lg">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm text-green-400">Stage Gate</span>
                  <CheckCircle className="w-4 h-4 text-green-400" />
                </div>
                <p className="text-xs text-slate-400">Invests at Series A</p>
              </div>
              <div className="p-3 bg-green-500/10 border border-green-500/20 rounded-lg">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm text-green-400">Portfolio</span>
                  <CheckCircle className="w-4 h-4 text-green-400" />
                </div>
                <p className="text-xs text-slate-400">No conflict detected</p>
              </div>
              <div className="mt-4 pt-4 border-t border-slate-700">
                <div className="flex items-center justify-between">
                  <span className="text-white font-medium">Overall Match</span>
                  <span className="text-2xl font-bold text-teal-400">78%</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Operon-powered Programmes Section */}
      <section className="max-w-7xl mx-auto px-6 py-20">
        <div className="relative overflow-hidden rounded-3xl p-8 md:p-12 border border-cyan-500/30 bg-gradient-to-br from-[#0B1735] via-[#0F1D40] to-[#14264F]">
          <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-cyan-500/10 blur-3xl pointer-events-none" />
          <div className="absolute -bottom-24 -left-24 w-72 h-72 rounded-full bg-blue-500/10 blur-3xl pointer-events-none" />
          <div className="relative text-center max-w-3xl mx-auto">
            <div className="inline-block text-[11px] tracking-[0.2em] font-mono text-cyan-300/80 uppercase mb-4 px-3 py-1 rounded-full border border-cyan-500/30 bg-cyan-500/5">
              Operon-powered programmes
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4 leading-tight">
              Change your gear. <span className="text-cyan-300">Discover the UK.</span>
            </h2>
            <p className="text-slate-300 leading-relaxed mb-8">
              Strategic programmes for accelerators, delegations and investor portfolios — combining expert-led therapeutic strategy with access to the UK&apos;s world-class ecosystem.
            </p>
            <Button
              onClick={() => navigate('/programmes')}
              className="bg-cyan-500 hover:bg-cyan-400 text-[#060B1C] font-semibold px-6 py-6 h-auto text-base rounded-xl"
              data-testid="btn-programmes"
            >
              Explore programmes
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-slate-700 py-8">
        <div className="max-w-7xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <img 
            src="/logo-complete.png" 
            alt="Operon" 
            className="h-[72px] object-contain"
          />
          <p className="text-sm text-slate-500">
            © {new Date().getFullYear()} Operon. Built for UK-based therapeutic founders.
          </p>
        </div>
      </footer>

      {/* Request Access Modal */}
      <RequestAccessModal
        open={!!accessFeature}
        onOpenChange={(o) => { if (!o) setAccessFeature(null); }}
        feature={accessFeature}
      />

      {/* Admin PIN Dialog */}
      <Dialog open={showAdminDialog} onOpenChange={setShowAdminDialog}>
        <DialogContent className="bg-slate-900 border-slate-700 sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Lock className="w-5 h-5 text-amber-400" />
              Admin Access
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Enter the 6-digit admin code to continue
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handlePinSubmit} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                placeholder="• • • • • •"
                value={pinCode}
                onChange={handlePinChange}
                className={`text-center text-2xl tracking-[0.5em] font-mono bg-slate-800 border-slate-600 text-white placeholder:text-slate-600 h-14 ${
                  pinError ? 'border-red-500 shake' : ''
                }`}
                autoFocus
                data-testid="admin-pin-input"
              />
              {pinError && (
                <p className="text-red-400 text-sm text-center">
                  Incorrect code. Please try again.
                </p>
              )}
            </div>
            
            <div className="flex gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowAdminDialog(false)}
                className="flex-1 border-slate-600 text-slate-300 hover:bg-slate-800"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={pinCode.length !== 6}
                className="flex-1 bg-amber-600 hover:bg-amber-700 text-white"
                data-testid="admin-pin-submit"
              >
                Enter
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
